import React, { FC } from 'react';
import styled, { css } from 'styled-components';

import { colorStack } from '../../../styleHelpers/colors';
import { IItem } from '../../../entities/IFilters';
import { RadioButton } from '../Inputs/RadioButton';
import IntlMessage from '../IntlMessage';
import { MultilineTextWithEllipsis } from '../TextWithEllipsis/MultilineTextWithEllipsis';

const OptionRow = styled.div<{ active?: boolean }>`
    font-weight: ${({ active }) => active ? 500 : 400};
    padding: 12px 1rem;
    min-height: 46px;
    justify-content: space-between;
    background: ${colorStack.white};
    transition: all .3s;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    &:hover {
        background: ${colorStack.lightBlue};
    }
    ${props => props.active && css`
        background: ${colorStack.lightBlue};
    `}
    > div:first-child {
        width: calc(100% - .5rem - 34px);
    }
    > div:nth-child(2) {
        width: calc(100% - 35px);
    }
`;

const ShadowLayer = styled.div`
    width: 97%!important;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 999;
`;

export interface IBooleanResultOptions {
    items: IItem[];
    selectedItems: IItem[];
    translationKey?: string;
    onSelectItem(item: IItem): void;
}

/**
 * Boolean filter options component – single value or no selection.
 * Displays a list with radio buttons, labels as in SingleResultElement.
 */
export const BooleanResultOptions: FC<React.PropsWithChildren<IBooleanResultOptions>> = props => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement>, elem: IItem) => {
        e?.stopPropagation();
        e?.preventDefault();
        props.onSelectItem(elem);
    };

    const getLabel = (elem: IItem) => {
        if (elem.data?.translationKey) {
            return <IntlMessage id={elem.data.translationKey} />;
        }
        if (props.translationKey) {
            return <IntlMessage id={`${props.translationKey}.${elem.data?.key ?? elem.data}`} />;
        }
        return elem.text;
    };

    return (
        <>
            {props.items?.map(elem => {
                const isSelected = !!props.selectedItems?.find(selectedItem => selectedItem.id === elem.id);
                return (
                    <OptionRow key={elem.id} active={isSelected}>
                        <ShadowLayer onClick={e => handleClick(e, elem)} />
                        <MultilineTextWithEllipsis numberOfLines={2}>
                            {getLabel(elem)}
                        </MultilineTextWithEllipsis>
                        <RadioButton checked={isSelected} />
                    </OptionRow>
                );
            })}
        </>
    );
};
